import React, { useState, useEffect } from 'react';
import { inputCls, labelCls } from '../shared';

const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Completed', 'Delayed'];

function statusStyle(s) {
  if (s === 'Completed') return 'badge-success';
  if (s === 'In Progress') return 'badge-primary';
  if (s === 'Delayed') return 'badge-danger';
  return 'badge-neutral';
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff === 0) return 'Today';
  return `${diff}d remaining`;
}

function daysColor(dateStr, status) {
  if (status === 'Completed') return 'text-[var(--adani-success)]';
  if (!dateStr) return 'text-[var(--adani-text-tertiary)]';
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return 'text-[var(--adani-danger)]';
  if (diff <= 3) return 'text-[var(--adani-warning)]';
  return 'text-[var(--adani-text-tertiary)]';
}

export default function MilestonesPage({ counts = {}, setCounts }) {
  const [milestones, setMilestones] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState('All');
  const [form, setForm] = useState({ title: '', programme: '', planned: '', actual: '', status: 'Not Started', owner: '', notes: '' });

  useEffect(() => {
    if (setCounts) {
      setCounts(prev => ({ ...prev, milestones: milestones.length }));
    }
  }, [milestones.length, setCounts]);

  const filtered = milestones.filter(m => statusFilter === 'All' || m.status === statusFilter);

  const handleSubmit = e => {
    e.preventDefault();
    setMilestones(prev => [{ ...form, id: Date.now() }, ...prev]);
    setForm({ title: '', programme: '', planned: '', actual: '', status: 'Not Started', owner: '', notes: '' });
    setShowForm(false);
  };

  const handleDelete = id => {
    setMilestones(prev => prev.filter(m => m.id !== id));
  };

  const toggleStatus = (id) => {
    setMilestones(prev => prev.map(m => {
      if (m.id !== id) return m;
      const order = STATUS_OPTIONS;
      const idx = order.indexOf(m.status);
      const next = order[(idx + 1) % order.length];
      return { ...m, status: next, actual: next === 'Completed' ? new Date().toISOString().split('T')[0] : m.actual };
    }));
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="font-google text-[22px] font-semibold text-[var(--adani-text-primary)] leading-tight">Milestone Tracking</h1>
          <p className="text-[13px] text-[var(--adani-text-secondary)] mt-1">Planned vs actual timelines across all programmes</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="topbar-filter">
            <option>All</option>
            {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
          </select>
          <button onClick={() => setShowForm(!showForm)} className="btn-primary text-[13px]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Milestone
          </button>
        </div>
      </div>

      {showForm && (
        <div className="glass-card-static rounded-2xl p-5 mb-5 animate-slide-up">
          <h3 className="text-[15px] font-semibold text-[var(--adani-text-primary)] mb-4">New Milestone</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Title</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className={inputCls} placeholder="Milestone name" required />
              </div>
              <div>
                <label className={labelCls}>Programme</label>
                <input value={form.programme} onChange={e => setForm(f => ({ ...f, programme: e.target.value }))} className={inputCls} placeholder="e.g. Mundra Port Expansion" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Planned Date</label>
                <input type="date" value={form.planned} onChange={e => setForm(f => ({ ...f, planned: e.target.value }))} className={inputCls} required />
              </div>
              <div>
                <label className={labelCls}>Status</label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className={inputCls + ' cursor-pointer'}>
                  {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Owner</label>
                <input value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} className={inputCls} placeholder="Person responsible" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className={inputCls} rows={2} placeholder="Additional context…" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-[13px]">Cancel</button>
              <button type="submit" className="btn-primary text-[13px]">Save Milestone</button>
            </div>
          </form>
        </div>
      )}

      <div className="glass-card-static rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr>
                <th className="table-header">Milestone</th>
                <th className="table-header">Programme</th>
                <th className="table-header">Status</th>
                <th className="table-header">Planned</th>
                <th className="table-header">Actual</th>
                <th className="table-header">Timeline</th>
                <th className="table-header w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => (
                <tr key={m.id} className="table-row">
                  <td className="table-cell">
                    <div>
                      <p className="text-[13px] font-medium text-[var(--adani-text-primary)]">{m.title}</p>
                      {m.owner && <p className="text-[11px] text-[var(--adani-text-tertiary)] mt-0.5">{m.owner}</p>}
                    </div>
                  </td>
                  <td className="table-cell text-[13px] text-[var(--adani-text-secondary)]">{m.programme || '—'}</td>
                  <td className="table-cell">
                    <button onClick={() => toggleStatus(m.id)} className={`badge ${statusStyle(m.status)} cursor-pointer hover:opacity-80 transition-opacity`}>
                      {m.status}
                    </button>
                  </td>
                  <td className="table-cell text-[13px] text-[var(--adani-text-secondary)] tabular-nums">{m.planned}</td>
                  <td className="table-cell text-[13px] text-[var(--adani-text-secondary)] tabular-nums">{m.actual || '—'}</td>
                  <td className={`table-cell text-[12px] font-medium tabular-nums ${daysColor(m.planned, m.status)}`}>
                    {m.status === 'Completed' ? 'Done' : daysUntil(m.planned) || '—'}
                  </td>
                  <td className="table-cell">
                    <button onClick={() => handleDelete(m.id)} className="btn-icon text-[var(--adani-text-tertiary)] hover:text-[var(--adani-danger)]">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state">
                      <p className="empty-state-title">No milestones yet</p>
                      <p className="empty-state-text">Click "Add Milestone" to track your first one</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
